import { useMemo, useState } from '@max/max';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { useRequest } from './useRequest';
import { API_PATH } from '../types/shared';
import { RewardType, PrizeStatus, AmountType } from '../types/rewardRecords';
import { getRequestBaseParams } from '../utils/request';
/** 优惠券分类 tab（用于奖励记录 - 优惠券部分） */
export const COUPON_PRIZE_TAB = [{
        label: '境内酒店',
        key: 'hotel'
    }, {
        label: '民宿',
        key: 'homeStay'
    }, {
        label: '景点',
        key: 'ticket'
    }, {
        label: '火车票',
        key: 'train'
    }, {
        label: '度假',
        key: 'vacation'
    }, {
        label: '机票',
        key: 'air'
    }];
export const COUPON_TAB_LIST = [{
        label: '全部',
        key: 'all'
    }, ...COUPON_PRIZE_TAB];
// todo 魔法数字
/** bizLine 值到 tab key 的映射（接口返回 Integer 类型） */
const BIZ_LINE_KEY_MAP = {
    1: 'hotel',
    2: 'air',
    3: 'train',
    4: 'homeStay',
    5: 'ticket',
    6: 'vacation'
};
/**
 * 对券列表进行排序：
 * 1. 过滤掉已过期的券（prizeStatus === EXPIRED）
 * 2. 未使用/已领取/可领取 排在前面，已使用 排在后面
 */
function filterAndSortCoupons(couponList) {
    return couponList.filter(item => item.prizeStatus !== PrizeStatus.EXPIRED).sort((a, b) => {
        const isUsedA = a.prizeStatus === PrizeStatus.USED ? 1 : 0;
        const isUsedB = b.prizeStatus === PrizeStatus.USED ? 1 : 0;
        return isUsedA - isUsedB;
    });
}
/** 根据 bizLine 字段将优惠券分类到各 tab */
function categorizeCoupons(couponList, couponTabList) {
    const result = {};
    couponTabList.forEach(t => {
        result[t.key] = [];
    });
    couponList.forEach(item => {
        if (!item)
            return;
        // result.all.push(item);
        const bizLine = item.bizLine;
        if (bizLine !== undefined && bizLine !== null) {
            const bizLineNum = Number(bizLine);
            const tabKey = BIZ_LINE_KEY_MAP[bizLineNum];
            if (tabKey && result[tabKey]) {
                result[tabKey].push(item);
                result.all.push(item);
            }
        }
    });
    // 对每个 tab 下的列表统一过滤 + 排序
    couponTabList.forEach(t => {
        result[t.key] = filterAndSortCoupons(result[t.key]);
    });
    return result;
}
function initEmptyCouponData(couponTabList) {
    const init = {};
    couponTabList.forEach(t => {
        init[t.key] = [];
    });
    return init;
}
export function useRewards(options) {
    const showCouponTabList = useMemo(() => {
        const { showCouponBizLineList } = options.feConfig;
        let result = COUPON_TAB_LIST;
        result = COUPON_TAB_LIST.filter(tab => showCouponBizLineList?.indexOf(tab.key) > -1);
        if (Array.isArray(result) && result.length > 1) {
            result = [COUPON_TAB_LIST[0], ...result];
        }
        return result;
    }, [options.feConfig]);
    const ctx = useMonitorEngineContext();
    const commonParams = ctx._runtimeInfo?.commonParams;
    const [rewardsData, setRewardsData] = useState([]);
    const [allCouponData, setAllCouponData] = useState(initEmptyCouponData(showCouponTabList));
    const { requestStatus, request } = useRequest(API_PATH.REWARDS);
    async function loadRewardsData() {
        try {
            const feCommonParams = await getRequestBaseParams(commonParams);
            const res = await request({
                ...feCommonParams,
                activityId: options.activityId
            });
            if (!Array.isArray(res?.rewardItems)) {
                throw new Error('获取奖励数据失败');
            }
            const list = res.rewardItems.filter(item => item.amountType === AmountType.THRESHOLD);
            setRewardsData(list);
            // 从奖励列表中提取优惠券（非免单卡）并按 bizLine 分类
            const couponItems = list.filter((item) => item.rewardType !== RewardType.FREE_CARD);
            setAllCouponData(categorizeCoupons(couponItems, showCouponTabList));
            ctx._raptorReport.monitorReportBizSuccess?.({
                apiPath: API_PATH.REWARDS
            });
        }
        catch (error) {
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API_PATH.REWARDS
            });
            ctx._raptorReport.monitorReportLog(error.message, error, {
                category: 'ajaxError',
                level: 'error'
            });
        }
    }
    const couponActiveIndex = options.couponActiveIndex ?? 0;
    const currentTab = showCouponTabList?.[couponActiveIndex];
    const displayedCouponList = currentTab ? Array.isArray(allCouponData[currentTab.key]) ? allCouponData[currentTab.key] : [] : [];
    return {
        showCouponTabList,
        loadRewardsData,
        requestStatus,
        rewardsData,
        allCouponData,
        displayedCouponList
    };
}
